import { getFreoConnectOffers } from '@/api';
import { get, isEmpty } from '@/lib/helpers';
import { FreoConnectOffersModel } from '@/interfaces/offers';
import { layoutConfig } from '@/lib/layout_consts';
import eventBus from '@/lib/event_bus';
import { store } from '@/store';
import ApplicationState from '@/business/models/application_state';

/**
 * Every route name that renders RejectScreen.vue (one per layout). The router
 * guard uses this to recognise a reject navigation before the screen mounts.
 */
export const REJECT_ROUTE_NAMES: string[] = [
  get(
    layoutConfig,
    'DEFAULT_LAYOUT.routes.rejectedApplication',
    'rejectedApplication'
  ),
  get(
    layoutConfig,
    'GPAY_LAYOUT.routes.rejectedApplication',
    'gpay-rejectedApplication'
  ),
].filter(Boolean);

// /connect/options is asked for twice on a rejection now — once by the router
// guard and once by RejectScreenHeader when the customer does stay on the
// screen. Cache the first answer so the customer only pays for one round trip.
let cachedOffers: FreoConnectOffersModel | null = null;
let cachedForCustomerId = '';
let inFlightRequest: Promise<FreoConnectOffersModel | null> | null = null;

const currentCustomerId = (): string => {
  const state: any = store.getState();
  return get(state, 'customer.customerId', '') || '';
};

export const getCachedFreoConnectOffers = (): FreoConnectOffersModel | null =>
  cachedOffers;

export const clearFreoConnectOffersCache = (): void => {
  cachedOffers = null;
  cachedForCustomerId = '';
  inFlightRequest = null;
};

export function hasPartnerOffers(
  offers: FreoConnectOffersModel | null
): boolean {
  const options = get(offers, 'personalLoanOptions', []);
  return Array.isArray(options) && options.length > 0;
}

/**
 * Fires the analytics event the reject screen used to own. It lives here so it
 * still fires on the path where the screen is skipped altogether.
 */
function trackOffersViewed(offers: FreoConnectOffersModel | null): void {
  const state: any = store.getState();
  eventBus.$emit('syncEvent', 'FCW.LOAN_OFFER_PAGE.VIEWED', {
    nonInteration: false,
    loanOfferCount: get(offers, 'personalLoanOptions', [])
      ? get(offers, 'personalLoanOptions', []).length
      : 0,
    phone: get(state, 'customer.customerDetails.phone', ''),
    customerId: get(state, 'customer.customerId', ''),
  });
}

export async function fetchFreoConnectOffers(): Promise<FreoConnectOffersModel | null> {
  // Tied to the customer it was fetched for, so a second login in the same tab
  // can never be handed the previous customer's offers.
  const customerId = currentCustomerId();
  if (cachedOffers && cachedForCustomerId === customerId) {
    return cachedOffers;
  }
  if (!inFlightRequest) {
    inFlightRequest = getFreoConnectOffers()
      .then((response: any) => {
        cachedOffers = get(response, 'data', null);
        cachedForCustomerId = customerId;
        trackOffersViewed(cachedOffers);
        return cachedOffers;
      })
      .catch(() => {
        console.log('Error occured while calling connect api');
        return null;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }
  return inFlightRequest;
}

/**
 * Connect answers on two domains, and a rejected customer has to be handed off
 * to the one they arrived on: web.moneytap.com to connect.moneytap.com, and
 * web.freo.money to connect.freo.money. Sending a freo.money customer to a
 * moneytap.com URL drops them on a brand they never chose, at the one moment
 * they are least inclined to give it the benefit of the doubt.
 *
 * VUE_APP_CONNECT_URL stays the default, so every host that is not freo.money
 * keeps the target it has today. VUE_APP_CONNECT_URL_FREO only overrides it for
 * the freo.money domain, and only when it is set — leaving it empty puts the
 * hand-off back on a single domain without a code change, which is the lever to
 * pull if connect.freo.money is ever not ready to receive traffic.
 */
export function connectBaseUrl(): string {
  const host = (window.location.hostname || '').toLowerCase();
  // the leading dot matters: a bare endsWith('freo.money') would also accept a
  // look-alike host such as notfreo.money
  const isFreoDomain = host === 'freo.money' || host.endsWith('.freo.money');
  const freoUrl = process.env.VUE_APP_CONNECT_URL_FREO;
  if (isFreoDomain && freoUrl) {
    return freoUrl;
  }
  return process.env.VUE_APP_CONNECT_URL || '';
}

export function connectOffersUrl(): string {
  const state: any = store.getState();
  return `${connectBaseUrl()}offers/?token=${get(
    state,
    'auth.access_token',
    ''
  )}`;
}

export function redirectToConnectOffers(): void {
  // replace() rather than href: the reject screen is the page we are trying not
  // to show, so it should not sit in history for Back to land on either.
  window.location.replace(connectOffersUrl());
}

/**
 * A rejected customer only gets forwarded to Connect on a plain rejection.
 * Re-apply, re-route and FreoPay pre-approval customers each get their own
 * header on the reject screen, so they have to stay on it. RejectScreen.vue
 * makes the same three checks; they are mirrored here so the router can reach
 * the same verdict before the screen is ever mounted.
 */
export function mustStayOnRejectScreen(): boolean {
  const state: any = store.getState();
  const customer = get(state, 'customer', null);
  // No customer in the store yet — the guard cannot judge, so let the screen
  // load and decide for itself as it did before.
  if (isEmpty(customer)) {
    return true;
  }
  // get() hands back a stored null as-is, so this cannot assume an array.
  const actionables = get(customer, 'actionables', []);
  const hasFreoPayPreApproval =
    Array.isArray(actionables) &&
    actionables.some(
      (actionable: any) =>
        get(actionable, 'type', '') === 'freopay_pre_approval'
    );
  return (
    get(customer, 'canReApply', false) ||
    get(customer, 'canReRoute', false) ||
    hasFreoPayPreApproval
  );
}

/**
 * An application that timed out rather than being turned down on merit. The
 * backend calls it EXPIRED; app_state_config's applicationStates map has no
 * entry for it, so the code is named here.
 */
export const APPLICATION_EXPIRED_STATUS_CODE = '90000.90000';

/**
 * True when the customer's application expired. Those customers are the ones
 * re-application is meant for, so they are never forwarded to Connect — the
 * reject screen has to load and offer them the re-apply action instead.
 *
 * Read from the stored customer payload first, with the ApplicationState
 * singleton as the fallback: the two are filled from the same big-cookie
 * response, but not always at the same moment, and either one answering is
 * enough to hold the customer back.
 */
export function isApplicationExpired(): boolean {
  const state: any = store.getState();
  const storedStatusCode = get(
    state,
    'customer.applicationState.applicationStatusCode',
    ''
  );
  if (storedStatusCode) {
    return storedStatusCode === APPLICATION_EXPIRED_STATUS_CODE;
  }
  return (
    ApplicationState.getInstance().applicationStatusCode ===
    APPLICATION_EXPIRED_STATUS_CODE
  );
}

/**
 * Send every rejected customer to Freo Connect, including the ones Connect has
 * no partner offers for, so the reject screen is never shown at all.
 *
 * Set to false to go back to forwarding only the customers who do have offers,
 * which leaves the reject screen in place for everyone else. That earlier
 * behaviour is what mustStayOnRejectScreen() and fetchFreoConnectOffers() below
 * are still here for.
 */
export const ALWAYS_REDIRECT_REJECTED_TO_CONNECT = true;

/**
 * True when the customer should be sent straight to Connect instead of being
 * shown the reject screen.
 */
export async function shouldSkipRejectScreen(): Promise<boolean> {
  const state: any = store.getState();
  // Both halves of the Connect URL have to be there. Without them the redirect
  // would be a relative path into this app, so leave the reject screen alone.
  // Asked through connectBaseUrl() so this judges the URL the customer would
  // actually be sent to on their domain, not just the default one.
  if (!connectBaseUrl()) {
    return false;
  }
  if (!get(state, 'auth.access_token', '')) {
    return false;
  }
  // Asked ahead of ALWAYS_REDIRECT_REJECTED_TO_CONNECT so it is actually
  // reached: an expired application is the case re-application exists for, and
  // forwarding it to Connect takes that action away with no way back to it.
  if (isApplicationExpired()) {
    return false;
  }
  if (ALWAYS_REDIRECT_REJECTED_TO_CONNECT) {
    // No /connect/options call: the answer cannot change the outcome any more,
    // and skipping it also removes the wait that used to precede the redirect.
    return true;
  }
  if (mustStayOnRejectScreen()) {
    return false;
  }
  return hasPartnerOffers(await fetchFreoConnectOffers());
}
